import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { Category } from "@insite/client-framework/Services/CategoryService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemStyle } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { StyledIcon } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal/Modal";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import Msq from "@MasterBluePrint/Components/Msq";
import productClickTracking from "@MasterBluePrint/Tracking/ProductClick/productClickTracking";


const enum fields {
    availableForRentTitle = "availableForRentTitle",
    availableForRentLinkText = "availableForRentLinkText",
    availableForRentLink = "availableForRentLink",
    scriptSrc = "scriptSrc",
    formID = "formID",
    loadFormParam1 = "loadFormParam1",
    loadFormParam2 = "loadFormParam2",
    loadFormParam3 = "loadFormParam3",
}



interface OwnProps extends WidgetProps {
    fields: {
        [fields.availableForRentTitle]: string;
        [fields.availableForRentLinkText]: string;
        [fields.availableForRentLink]: string;
        [fields.scriptSrc]: string;
        [fields.formID]: string;
        [fields.loadFormParam1]: string;
        [fields.loadFormParam2]: string;
        [fields.loadFormParam3]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const category = getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value;
    
    return {
        currentPageUrl,
        category,
        productInfosById: state.pages.productDetails.productInfosById,
    };
};

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface AvailableForRentStyle {
    availableForRentMainContainer?: InjectableCss;
    availableForRentModal?: InjectableCss;
    availableForRentHeading?: InjectableCss;
    hideAvailableForRentHeading?: InjectableCss;
    rentalConditionWrapper?: InjectableCss;
    rentalConditionHeader?: InjectableCss;
    availableForRentLink?: InjectableCss;
    oldAvailableForRentLink?: InjectableCss;
    closeContainer?: InjectableCss;
    cancelButton?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const minMulQtyStyles: AvailableForRentStyle = {
    availableForRentMainContainer: {
        css: css`
            font-weight: 600;
            margin-bottom: .2em;
            text-align: center;
            @media screen and (max-width: 991px) {
                display:none;
            }
            > div {
                width:100% !important;
                margin:0 auto;
                padding: 0;
                justify-content: center;
            }
            > div > div {width:100%;}
            > div > div:nth-of-type(1) {                
                padding:13px 3px 0;
                @media screen and (max-width: 991px) {
                    padding:0 3px 0;
                }
            }
            > div > div:nth-of-type(2) {
                padding:0 3px;
            }
            > div > div:has(.oldRentLink) {
                padding:0 3px;
            }
        `,
    },
    availableForRentHeading: {
        css: css`
            font-family: 'Roboto', sans-serif;
            font-size: 1.25rem;
            color: #333F48;
            font-weight: 500;
            letter-spacing: -.024rem;
            line-height: 1.4375rem;
            margin-bottom: .31250625rem;
            margin-top: 3.063rem;
            width: 100%;
        `,
    },
    hideAvailableForRentHeading: {
        css: css`
            display:none;
        `,        
    },
    rentalConditionWrapper: {
        css: css`
            display:block;
            width:100%;
            text-align:left;
            margin-top:9px;
            border-top:1px solid rgba(204, 204, 204, 1);
            padding:13px 3px;
        `,
    },
    rentalConditionHeader: {
        css: css`
            display:inline-block;
            margin-bottom:4px;
            font-family:'Lato', sans-serif;
            font-size:.8125rem;
            color:rgb(34,34,34);
            font-weight:700;
            letter-spacing:.01625rem;
            margin-top:13px;
        `,
    },
    availableForRentLink: {
        css: css`
            display: flex;
            padding: 8px 16px;
            justify-content: center;
            align-items: center;
            gap: 10px;
            align-self: stretch;
            display:block;
            width:100%;
            margin:0;
            border:1px solid #001d42;
            border-radius:2px;
            background:#002554;
            > span {
                color:#FFF !important;
                text-align: center;
                font-family: Roboto;
                font-size: 15px;
                font-style: normal;
                font-weight: 500;
                line-height: 20px;
                text-transform: uppercase;
            }
            :hover,:active {
                border:1px solid #005cb9;
                background: #005cb9;
                > span {
                    color:#FFF !important;
                }
            }
        `,
    },
    oldAvailableForRentLink: {
        css: css`
            color: #333F48;
            font-family: Lato;
            font-size: .875rem;
            letter-spacing: 0;
            line-height: 1.5rem;
            text-decoration: underline;

            span {
                color: #333F48 !important;
            }
        `,
    },
    availableForRentModal: {
        css: css`
            margin-top: 0;
            display: flex;
            -webkit-box-align: center;
            align-items: center;
            -webkit-box-pack: center;
            justify-content: center;
            height: 100%;
            animation: 300ms ease 0s 1 normal none running fUZMVa;

            &.available-rent-modal {
                #modalTitle {
                    display: none;
                }

                @media screen and (min-width: 768px) {
                    > div  {
                        height: auto;
                        max-height: calc(100% - 90px);
                        margin: 2.8125rem;
                        max-width: 31.25rem;
                        box-shadow: 0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%);
                        background: #fff;
                    }
                }
            }

            > div > div {
                position: relative;
            }

            > div > div > div {
                min-height: 170px;
            }

            form.mktoForm .mktoButtonRow {
                border-top: 1px solid rgba(215, 215, 215, .72);
                padding-top: .9375rem;
            }

            h1 {
                color: #333F48;
                font-family: 'Roboto', sans-serif;
                font-size: 1.25rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 2rem;
                position: relative;
                margin-top: 1.5625rem;

                &:after {
                    height: .125rem;
                    width: 2.75rem;
                    background-color: #C63527;
                    content: '';
                    display: inline-block;
                    position: absolute;
                    bottom: -.75rem;
                    left: .0625rem;
                }
            }

            form.mktoForm button.mktoButton {
                float: right;

                @media screen and (max-width: 767px) {
                    float: none;
                }
            }

            .mktoRequiredField .mktoAsterix{
                font-size: .9375rem !important;
                display: block !important;
                color: #535E64 !important;
            }

            .mktoLabel {
                color: #535E64 !important;
                font-family: 'Roboto', sans-serif;
                font-size: .9375rem;
                font-weight: 500 !important;
                letter-spacing: .012rem;
                line-height: 1.4375rem;

                b,
                strong,
                span {
                    color: #535E64 !important;
                    font-family: 'Roboto', sans-serif;
                    font-size: .9375rem;
                    font-weight: 500 !important;
                    letter-spacing: .012rem;
                }

                em {
                    // display: none !important;
                    padding-left: 0; 
                    padding-right: 0; 
                }
            }

            .mktoFormCol {
                margin-bottom: .625rem !important;
            }

            form.mktoForm .mktoRadioList label[for], 
            form.mktoForm .mktoCheckboxList label[for] {
                padding: 0 0 .9em 1.75em !important;
            }

            form {
                padding: 35px 0 20px 0;

                &.mktoForm {
                    padding: 35px 0 20px 0;
                }
            }

            form.mktoForm input[type=url], 
            form.mktoForm input[type=text], 
            form.mktoForm input[type=date], 
            form.mktoForm input[type=tel], 
            form.mktoForm input[type=email], 
            form.mktoForm input[type=number], 
            form.mktoForm textarea.mktoField, 
            form.mktoForm select.mktoField {
                height: 2.5rem;
                border: 1px solid #CECECE;
                border-radius: .125rem;
                background-color: #fff;
                min-height: 2.5rem;
            }
        `,
    },
    cancelButton: {
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: .9375rem;
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-align: center;
            text-decoration: underline;
            cursor: pointer;
            margin-left: 3em;
            margin-right: 3em;
            justify-content: center;
            width: auto;
            position: absolute;
            bottom: 3.875rem;

            @media screen and (max-width: 767px) {
                position: static;
                margin: .9375rem auto 0;
            }
        `,
    },
    closeContainer: {
        css: css`
           cursor: pointer;
           position: absolute;
           right: 1.25rem;
           top: 1.5rem;
           .close-label {
                color: #535E64;
                font-family: 'Roboto', sans-serif;
                font-size: .875rem;
                letter-spacing: .01rem;
                line-height: 1;
                display: inline-block;
                margin-right: .625rem;
                vertical-align: middle;
           }
           .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                }
            }
        `,
    },
    spinner: {
        css: css`
            margin: 1.5rem auto;
            display: block;
        `,
    }
};

const styles = minMulQtyStyles;

const AvailableForRent: React.FC<Props> = ({ fields, product, currentPageUrl, category, productInfosById }) => {
    if (product.properties.availableForRent === undefined) {
        return null;
    }
    if (product.properties?.availableForRent.toLowerCase() === "true") {
        const handleProductClick = () => {
            const prodNewInfo = productInfosById?.[product.id];
            if (!prodNewInfo) {
                return;
            }
            const productContextModel = {
                product,
                productInfo: prodNewInfo,
                idx: 0,
            };
            const warehouses = prodNewInfo?.inventory?.inventoryWarehousesDtos?.[0]?.warehouseDtos || undefined;
            const inventory = prodNewInfo?.inventory;

            productClickTracking(
                productContextModel,
                "", 
                "", 
                category,
                currentPageUrl,
                { currentPageUrl, category, productInfosById } as any, 
                warehouses, 
                inventory, 
                "Available For Rent Button",
                product.id
            );
        };

        const availableForRent = () => {
            setIsavailableForRentModalOpen(true);
            document.getElementById("triangle")?.toggleAttribute("hidden");
            document.getElementById("form")?.toggleAttribute("hidden");
            document.getElementById("overlay")?.toggleAttribute("hidden");
            const formLoadingSpinner = document.getElementById("formLoadingSpinner");
            if (formLoadingSpinner) {
                formLoadingSpinner.style.display = "block";
            }
            handleProductClick();
        };
        const [isavailableForRentModalOpen, setIsavailableForRentModalOpen] = useState(false);
        const onAvailableForRentClose = () => {
            setIsavailableForRentModalOpen(false);
        };


        const [isLoaded, setIsLoaded] = useState(false);
        const marketoScriptId = 'mktoForms';
        const formId = fields.loadFormParam3;
        useEffect(() => {
            if (!document.getElementById(marketoScriptId)) {
                loadScript();
            } else {
                setIsLoaded(false);
            }
        }, []);

        useEffect(() => {
            const scriptCall = document.createElement("script");
            scriptCall.innerHTML =
                `MktoForms2.loadForm("${fields.loadFormParam1}", "${fields.loadFormParam2}", "${fields.loadFormParam3}");
             MktoForms2.whenReady(function(mktoForm){
                const defaultValues = {
                    buy1Mod : "${product.productNumber}",                  
                };
              
                let currentValues = mktoForm.getValues();
              
                let finalValues = Object.keys(defaultValues)
                  .reduce(function(acc,key){
                     if( !currentValues[key] ) {
                       acc[key] = defaultValues[key];
                     }
                     return acc;
                  },{});
              
                mktoForm.setValues(finalValues);        
              });
              MktoForms2.whenReady(function(mktoForm){
                document.getElementById("formLoadingSpinner").style.display = "none";
              });
              `;
            document.body.appendChild(scriptCall);
        }, [isLoaded, formId, isavailableForRentModalOpen]);

        const loadScript = () => {
            const s = document.createElement('script');
            s.id = marketoScriptId;
            s.type = 'text/javascript';
            s.async = true;
            s.src = fields.scriptSrc;
            s.onload = () => setIsLoaded(true);
            document.getElementsByTagName('head')[0].appendChild(s);
        };
        
        const featureFlags: string[] = ["TEUS-8695"];
        const flagValues = useLaunchDarklyFlags({ featureFlags });
        
        const [shouldRender, setShouldRender] = useState(false);
        useEffect(() => {
            const timer = setTimeout(() => {
                setShouldRender(true);
            }, 1000);
            return () => clearTimeout(timer);
        }, []);
        if (!shouldRender) {
            return null;
        }
        
        const ldNewRentalVisibility = flagValues["TEUS-8695"];
        const ifOtherConditions = document.querySelector(".otherConditionsList");

        return (
            <GridContainer {...styles.availableForRentMainContainer}>
                {!ldNewRentalVisibility && (
                    <Typography as="h5" {...styles.availableForRentHeading}>
                        {fields.availableForRentTitle}
                    </Typography>
                )}     

                {ldNewRentalVisibility && !ifOtherConditions && (
                    <StyledWrapper {...styles.rentalConditionWrapper}>
                        <Typography {...styles.rentalConditionHeader}>Other Buying Options:</Typography>
                    </StyledWrapper>
                )}        

                <Typography as="div">
                    {ldNewRentalVisibility && ifOtherConditions? (
                        <Link
                            type="button"
                            onClick={availableForRent}
                            data-test-selector="availableForRent"
                            className="yesFlagYesOther"
                            {...styles.availableForRentLink}
                        >
                            {fields.availableForRentLinkText}
                        </Link>
                    ) : ldNewRentalVisibility && !ifOtherConditions? (
                        <Link
                            type="button"
                            onClick={availableForRent}
                            data-test-selector="availableForRent"
                            {...styles.availableForRentLink}
                        >
                            {fields.availableForRentLinkText}
                        </Link>
                    ) : (
                        <Link
                            type="button"
                            onClick={availableForRent}
                            data-test-selector="availableForRent"
                            {...styles.oldAvailableForRentLink}
                            className="oldRentLink"
                        >
                            Submit Rental Request
                        </Link>
                    )}      
                                  
                    <Modal
                        isOpen={isavailableForRentModalOpen}
                        handleClose={() => onAvailableForRentClose()}
                        data-test-selector="availableForRentModal"
                        {...styles.availableForRentModal}
                        className="available-rent-modal"
                    >
                        <Typography as="div" onClick={onAvailableForRentClose} {...styles.closeContainer}>
                            <span className="close-label">Close</span>
                            <Icon className="icon-close" src="X" />
                        </Typography>
                        <div className="container">
                            <h1>Rental Inquiry</h1>
                            <LoadingSpinner {...styles.spinner} id="formLoadingSpinner"/>
                            <form id={`mktoForm_${formId}`}></form>
                            <Clickable onClick={onAvailableForRentClose} {...styles.cancelButton}>{translate("Cancel")}</Clickable>
                        </div>

                    </Modal>
                </Typography>
            </GridContainer>
        );
    }
    return <></>;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(AvailableForRent)),
    definition: {
        displayName: "Available For Rent",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
        fieldDefinitions: [
            {
                name: fields.availableForRentTitle,
                editorTemplate: "TextField",
                defaultValue: "Rent Instead",
                fieldType: "Translatable",
            },
            {
                name: fields.availableForRentLinkText,
                editorTemplate: "TextField",
                defaultValue: "Click here for more information",
                fieldType: "Translatable",
            },
            {
                name: fields.availableForRentLink,
                editorTemplate: "TextField",
                defaultValue: "/",
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.scriptSrc,
                editorTemplate: "TextField",
                displayName: "Script Src",
                defaultValue: "//app-sj04.marketo.com/js/forms2/js/forms2.min.js",
            },
            {
                fieldType: "General",
                name: fields.formID,
                editorTemplate: "TextField",
                displayName: "Form ID",
                defaultValue: "mktoForm_5494",
            },
            {
                fieldType: "General",
                name: fields.loadFormParam1,
                editorTemplate: "TextField",
                displayName: "Load Form Param 1",
                defaultValue: "//app-sj04.marketo.com",
            },
            {
                fieldType: "General",
                name: fields.loadFormParam2,
                editorTemplate: "TextField",
                displayName: "Load Form Param 2",
                defaultValue: "313-KJQ-147",
            },
            {
                fieldType: "General",
                name: fields.loadFormParam3,
                editorTemplate: "TextField",
                displayName: "Load Form Param 3",
                defaultValue: "5494",
            },

        ],

    },
};
export default widgetModule;